<script setup>
import GenericCard from "@/components/ui/card/GenericCard.vue";
import { ref, computed, watch } from "vue";
import { Check } from "lucide-vue-next";
import { useLocalization, useConfig } from "@/composables";
import Button from "@/components/ui/button/Button.vue";
import GenericAccordion from "@/components/ui/accordion/GenericAccordion.vue";

const { numberToMoney } = useLocalization();
const { enterpriseSubscriptionMonthlyPrice } = useConfig();

const props = defineProps({
    loading: {
        type: Boolean,
        default: false
    },
})

const emit = defineEmits(['select:plan'])

const planName = 'Enterprise'
const planSlug = 'enterprise'
const planDescription = 'I manage a collection of markets, or I oversee markets across different locations.'

const isYearly = ref(false);

const features = computed(() => {
    const paidPrice = isYearly.value ? 0 : enterpriseSubscriptionMonthlyPrice;
    return [
        {
            type: 'Free',
            price: 0,
            description: 'For markets that want more control and a branded experience.',
            items: [
                'Set up your organization',
                'Add a small number of markets',
                'Invite admins and agents',
                'Manage buyers and sellers',
                'Manage buyers and sellers'
            ]
        },
        {
            type: 'Paid',
            price: numberToMoney(paidPrice, '', 0),
            description: 'For markets that want more control and a branded experience.',
            items: [
                'One time user verification',
                'White Labelling on association level',
                'Small fee per transaction (0.5%)',
                'Shared insights and communications dashboards',
                'Covers 25 markets, extra markets available for a small monthly fee',
                'Verification is optional'
            ]
        }
    ]
})

const plan = computed(() => {
    return {
        yearly: isYearly.value,
        name: planSlug,
    }
})

const selectPlan = () => {
    emit('select:plan', plan.value);
}
</script>

<template>
    <div class="w-full max-w-2xl mx-auto">
        <GenericCard>
            <template #header>
                <div class="flex flex-col justify-center items-center">
                    <div class="text-center text-2xl font-medium text-primary-font">{{ planName }}</div>
                    <div class="flex w-full lg:w-sm justify-center text-center text-xs text-primary-gray">{{ planDescription }}</div>
                </div>
            </template>
            <template #content>
                <div>
                    <!-- Web View -->
                    <div class="hidden lg:flex flex-col lg:flex-row justify-between items-start mt-6 gap-6">
                        <div v-for="feature in features" :key="feature.type" class="flex flex-col justify-between">
                            <div class="flex flex-row justify-between items-center">
                                <div class="text-sm font-medium text-primary-font">
                                    {{ feature.type }}
                                </div>
                                <div v-if="feature.price" class="text-base text-primary-green">
                                    <span>
                                        {{ feature.price }}
                                    </span>
                                    <span v-if="isYearly" class="text-xs text-primary-gray font-medium">/year</span>
                                    <span v-else class="text-xs text-primary-gray font-medium">/month</span>
                                </div>
                            </div>
                            <div class="text-xs text-primary-gray my-4">
                                {{ feature.description }}
                            </div>
                            <div class="flex flex-col bg-neutral-50 rounded-lg p-4">
                                <div v-if="feature.type === 'Paid'" class="flex flex-row items-center gap-2 py-2">
                                    <span class="text-xs text-primary-font">
                                        Everything in Free, plus
                                    </span>
                                </div>
                                <div v-for="item in feature.items" :key="item" class="flex flex-row items-center gap-2 py-2">
                                    <div>
                                        <Check class="text-white bg-green-500 rounded-full p-0.5" size="16" />
                                    </div>
                                    <span class="text-xs text-primary-gray">
                                        {{ item }}
                                    </span>
                              </div>
                          </div>
                        </div>
                    </div>
                    <!-- Mobile View -->
                     <div class="flex flex-col lg:hidden">
                        <GenericAccordion :title="feature.type" :value="feature.type" v-for="feature in features" :key="feature.type">
                            <template #title>
                                <div class="flex flex-col justify-start items-start">
                                    <div class="text-sm font-medium text-primary-font mb-2">
                                        {{ feature.type }}
                                    </div>
                                    <div v-if="feature.price" class="text-base text-primary-green mb-2 flex items-center gap-2">
                                        <div class="">
                                            <span>{{ feature.price }}</span>
                                            <span class="text-xs text-primary-gray font-medium">/month</span>
                                        </div>
                                    </div>
                                    <div class="text-xs text-primary-gray text-start mb-2">
                                        {{ feature.description }}
                                    </div>
                                </div>
                            </template>
                            <template #default>
                                <div class="flex flex-col bg-neutral-50 rounded-lg p-4">
                                    <div v-if="feature.type === 'Paid'" class="flex flex-row items-center gap-2 py-2">
                                        <span class="text-xs text-primary-font">
                                            Everything in Free, plus
                                        </span>
                                    </div>
                                    <div v-for="item in feature.items" :key="item" class="flex flex-row items-center gap-2 py-2">
                                        <div>
                                            <Check class="text-white bg-green-500 rounded-full p-0.5" size="16" />
                                        </div>
                                        <span class="text-xs text-primary-gray">
                                            {{ item }}
                                        </span>
                                  </div>
                              </div>
                            </template>
                        </GenericAccordion>
                     </div>
                </div>
            </template>
          <template #footer>
            <div class="w-full">
                <div class="flex flex-col justify-center items-center">
                    <div class="text-sm text-primary-gray">Free and paid options are available.</div>
                    <div class="mt-2 w-full">
                        <Button 
                            :disabled="loading"
                            variant="outline" 
                            class="w-full shadow-none border border-primary-border font-semibold text-sm" 
                            @click="selectPlan"
                        >
                            {{loading ? 'Processing...' : 'Get Started'}}
                        </Button>
                    </div>
                </div>
            </div>
          </template>
        </GenericCard>
    </div>
</template>